export const ENRICHMENT_RULES = [
  {
    id: "property",
    name: "Property Enrichment",
    scope: "PropertyAsset",
    icon: "Building",
    accent: "#0ea5e9",
    summary:
      "Turn a row from a raw loan/sales spreadsheet into a fully-formed property record with owner, sale history, and a clean address.",
    inputs: [
      { label: "Property Address", note: "street + city + state + zip" },
      { label: "Property Type", note: "Office / Industrial / Retail / Multifamily" },
      { label: "Market Name + Submarket", note: "e.g. Silicon Valley / North San Jose" },
      { label: "County Name", note: "set onto identity.address.county" },
      { label: "Last Sale Date + Last Sale Price", note: "folded into details.sales[]" },
      { label: "Owner Name + Owner Phone + Owner Contact", note: "details.owner.{name,phone,contact}" },
    ],
    sources: [
      { name: "Santa Clara County 4YR Loan Excel", trust: "verified", note: "Primary source — every row gets source-stamped onto contact reach entries." },
      { name: "LoopNet company pages", trust: "high", note: "Used to confirm CRE firm activity, HQ phone." },
      { name: "Public news (sfyimby, connectcre, therealdeal)", trust: "high", note: "Used to corroborate ownership and intent of buyer." },
      { name: "Company websites (firm domain)", trust: "high", note: "Source for company contact pages, leadership rosters." },
      { name: "BBB profiles", trust: "medium", note: "Cross-checks firm HQ + principals." },
      { name: "OpenCorporates / CA SOS bizfile", trust: "medium", note: "Agent for service, registered officers — partially paywalled." },
    ],
    transforms: [
      "Address parsing → identity.address.{streetNo, streetName, street, city, state, zip, county}",
      "Sale rows for same address are merged into details.sales[] (sorted by date)",
      "Owner phone digits-only normalised; stored as details.owner.phone",
      "Email pattern inference: if firm domain known, generate firstname.lastname@domain (confidence='inferred')",
      "Per-channel source + confidence stamped on reach.emails[] / reach.phones[] entries",
    ],
    dedupe: [
      "Key = (identity.address.street + identity.address.city) — case-insensitive",
      "Duplicate rows in source are NOT inserted as new docs; they roll into details.sales[]",
      "contactList and companyList are unioned (by contactId / name)",
    ],
    fieldsWritten: [
      "identity.address.{street, streetNo, streetName, city, state, zip, county}",
      "identity.apn (currently blank — APN not in source file; needs assessor lookup)",
      "details.propertyType",
      "details.submarket",
      "details.owner.{name, phone, contact}",
      "details.sale (latest sale, for backwards compat)",
      "details.sales[] (full history)",
      "details.enrichment.{bucket, tier, confidence, flags, notes, sourceUrls}",
    ],
    confidence: [
      { label: "verified", color: "#16a34a", meaning: "Came directly from the source Excel or a primary public record." },
      { label: "high",     color: "#16a34a", meaning: "Confirmed via two independent public sources." },
      { label: "inferred", color: "#d97706", meaning: "Derived from a pattern (e.g. email = firstname.lastname@firmdomain)." },
      { label: "low",      color: "#dc2626", meaning: "Single weak signal — surfaced for review, not for outreach." },
    ],
    openIssues: [
      "APN is 0% populated — needs Santa Clara County Assessor lookup per address.",
      "Only 9.6% of linked contacts have an email; pattern-inference pass not yet run on the 80 contacts with company + no email.",
    ],
  },
  {
    id: "contact",
    name: "Contact Enrichment",
    scope: "Contact",
    icon: "People",
    accent: "#7c3aed",
    summary: "Take an owner-of-record name (often an LLC) and surface a real principal, work email, and direct phone.",
    inputs: [
      { label: "Owner Name (LLC or person)", note: "from property.details.owner.name" },
      { label: "Owner Phone", note: "from property.details.owner.phone" },
      { label: "Property Address", note: "geographic anchor for SoS / assessor lookups" },
    ],
    sources: [
      { name: "Company website / About / Leadership pages", trust: "high" },
      { name: "City business directories (e.g. Santa Clara)", trust: "high" },
      { name: "BBB profile", trust: "medium" },
      { name: "LinkedIn (public previews only)", trust: "medium" },
      { name: "OpenCorporates / CA SOS bizfile", trust: "medium", note: "agent-for-service often reveals the real principal" },
      { name: "Public news / press releases", trust: "high" },
    ],
    transforms: [
      "LLC name → registered agent → principal name (via SoS lookup)",
      "Principal name + firm domain → email pattern (firstname.lastname / firstinitial+lastname / first / firstname)",
      "Phone: digits-only, formatted on render; office phone differentiated from direct line",
    ],
    fieldsWritten: [
      "identity.person.{firstName, lastName, title}",
      "identity.company.{name}",
      "reach.emails[] (with per-item source + confidence)",
      "reach.phones[] (with per-item source + confidence)",
      "source (top-level provenance string)",
      "confidence (top-level: verified / high / inferred / low)",
    ],
    confidence: [
      { label: "verified", color: "#16a34a", meaning: "Email/phone was published on a primary source page." },
      { label: "high",     color: "#16a34a", meaning: "Multiple independent sources agree." },
      { label: "inferred", color: "#d97706", meaning: "Pattern-derived (e.g. firstname.lastname@firmdomain)." },
      { label: "low",      color: "#dc2626", meaning: "Single weak source — flagged for human review." },
    ],
    openIssues: [
      "6 LLC-only properties still have no person name (Capitol Square, Evergreen Valley, Grade-way x2, Alviso Taylor, Seven Hills).",
    ],
  },
];

export const findRule = (id) => ENRICHMENT_RULES.find((r) => r.id === id);
